// src/components/Layout.jsx
import React, { useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import SideMenu from "./SideMenu";
import Header from "./Header";
import { API_ORIGIN } from "../config/apiConfig";
import "../styles/themeOverrides.css";
// NOTE: sideMenu.css / appLayout.css / header.css (this app's own
// hand-written CSS from before the theme) are still NOT imported here
// on purpose -- appLayout.css in particular has real hazards left in
// it (a global `overflow: hidden`, a blanket `textarea` height cap,
// broad `.table`/`.form-control`/`.modal` overrides) that would fight
// the theme's own styling if brought back wholesale. The theme's
// compiled CSS (vendors.min.css + app.min.css) is loaded globally from
// public/index.html instead -- see HTML-REDESIGN-NOTES.md.
//
// themeOverrides.css (imported above) is the "ported over" file this
// comment used to say didn't exist yet -- it's new, small on purpose,
// and is where every small rule this project's own pages need on top
// of the theme (brand-teal buttons/text, Account > User's custom role
// tabs, the wizard step indicator's current-step highlight) actually
// lives and actually loads. If a page needs a new custom rule, add it
// there, not to appLayout.css -- nothing imports that file, so nothing
// added to it renders.

export default function Layout() {
  // Reads the logged-in user cached in localStorage("currentUser").
  // That key is written in two places:
  //   - OtpVerify.jsx, right after a successful /verify-otp
  //     (localStorage.setItem("currentUser", JSON.stringify(data.user))
  //     -- data.user already comes back with {id, email, UserName,
  //     RoleName} from login_api.py). That one's fine as-is: it happens
  //     right before navigating to /dashboard, which mounts Layout fresh.
  //   - ProfilePage.jsx, after a successful photo upload, so the new
  //     photo shows up here without needing to log in again.
  //
  // FIX: Layout stays mounted across route changes (react-router just
  // swaps the <Outlet /> below, per the theme's sidebar+topbar layout),
  // so a plain `JSON.parse(localStorage...)` read at the top of the
  // function body only ever ran again if Layout itself happened to
  // re-render for some unrelated reason -- navigating to Profile,
  // uploading a photo, and clicking back to Dashboard never re-triggered
  // it, so the header avatar kept showing the old photo/initials until a
  // full page reload. Now it's real state, refreshed on a
  // "currentUserUpdated" event that ProfilePage.jsx dispatches right
  // after its own localStorage.setItem (and on "storage", so a photo
  // change in another tab updates this one's header too), instead of
  // silently going stale until something else forces Layout to re-run.
  const [storedUser, setStoredUser] = useState(() =>
    JSON.parse(localStorage.getItem("currentUser") || "null")
  );

  useEffect(() => {
    const refreshStoredUser = () => {
      setStoredUser(JSON.parse(localStorage.getItem("currentUser") || "null"));
    };
    window.addEventListener("currentUserUpdated", refreshStoredUser);
    window.addEventListener("storage", refreshStoredUser);
    return () => {
      window.removeEventListener("currentUserUpdated", refreshStoredUser);
      window.removeEventListener("storage", refreshStoredUser);
    };
  }, []);

  const currentUser = {
    userName: storedUser?.UserName || "Guest",
    userRole: storedUser?.RoleName || "",
    userAvatarUrl: storedUser?.ProfileImage
      ? `${API_ORIGIN}/profile_photo/${storedUser.ProfileImage}`
      : "",
    notificationCount: 0
  };

  // Sidebar pin/unpin state, lifted up here so it's a single source of
  // truth (SideMenu.jsx's pin button is the only thing that reads/sets
  // it now, but keeping it here rather than local to SideMenu leaves
  // room for something else to react to it later without another
  // refactor).
  //
  // This now uses the theme's own "on-hover" / "on-hover-active"
  // mechanism directly (see CET-HTML-Code's sidenav-menu markup and the
  // .button-on-hover / .btn-on-hover-icon rules in app.min.css) instead
  // of the "condensed" mode used before -- matching the reference
  // recording exactly:
  //   - sidebarCondensed = false (pinned) -> "on-hover-active": sidebar
  //     stays fully expanded, icon + label, all the time. The pin
  //     button's ring gets a filled center dot (pure CSS, off this same
  //     attribute -- see html[data-sidenav-size=on-hover-active]
  //     .button-on-hover .btn-on-hover-icon::before in app.min.css).
  //   - sidebarCondensed = true (unpinned) -> "on-hover": sidebar sits
  //     narrow/icon-only, and app.min.css alone expands it to full
  //     width the instant the mouse moves anywhere over it (as an
  //     overlay -- position:fixed with a raised z-index/shadow, so nothing
  //     else on the page reflows or shifts), then narrows straight back
  //     down the instant the mouse leaves. No extra JS needed for that
  //     hover behavior at all, it's already built into the theme's CSS
  //     for this exact attribute value.
  // The old topbar hamburger button doesn't fit into this model --
  // app.min.css hides .sidenav-toggle-button unconditionally under both
  // of these attribute values, since the theme intends .button-on-hover
  // to be the only control once you're using this mechanism -- so it's
  // been removed from Header.jsx rather than left permanently invisible.
  const [sidebarCondensed, setSidebarCondensed] = useState(false);

  // Keeps html[data-sidenav-size] in sync with sidebarCondensed at all
  // times, including on first mount -- overriding whatever the theme's
  // own startup script (config.js, loaded in public/index.html) set
  // before React ran. That script's own default happens to also be
  // "on-hover-active" (matching sidebarCondensed's default of false
  // here), but this doesn't rely on that coincidence.
  useEffect(() => {
    document.documentElement.setAttribute(
      "data-sidenav-size",
      sidebarCondensed ? "on-hover" : "on-hover-active"
    );
  }, [sidebarCondensed]);

  const toggleSidebar = () => setSidebarCondensed((prev) => !prev);

  return (
    // "wrapper" / "content-page" / "container-fluid" are the theme's
    // own class names (see dashboard.html in CET-HTML-Code) -- app.min.css
    // targets these specifically for the topbar+sidebar+content layout,
    // spacing, and the sidebar-collapse behavior driven by app.js.
    <div className="wrapper">
      <Header
        userName={currentUser.userName}
        userRole={currentUser.userRole}
        userAvatarUrl={currentUser.userAvatarUrl}
        notificationCount={currentUser.notificationCount}
      />
      <SideMenu sidebarCondensed={sidebarCondensed} onToggleSidebar={toggleSidebar} />

      <div className="content-page">
        <div className="container-fluid">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
